/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { pd } from '@airasia-rn/ui';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import type { FeedHeaderProps } from './types';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { getColor, getTextStyle } from '@airasia-phoenix/react-native/utils';
import { useSocialAnalytics } from '../../analytics';

export const FeedHeaderView = ({
  title,
  sectionId,
  deeplink,
  location,
  onPress,
}: FeedHeaderProps) => {
  const { trackFeedHeaderTap } = useSocialAnalytics();
  const handlePress = () => {
    if (onPress) {
      trackFeedHeaderTap(sectionId, location?.country ?? '');
      onPress(deeplink, location, sectionId);
    }
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={!onPress}
      testID="feed-header-touchable"
    >
      <View style={styles.header}>
        <View style={styles.titleContainer}>
          <TextView
            text={title}
            numberOfLines={1}
            style={[
              getTextStyle(
                pd.sys.typescale.body_text_medium,
                pd.sys.colour.content_primary,
              ),
            ]}
          />
        </View>
        {onPress && (
          <IconView
            height={24}
            width={24}
            tint={getColor(pd.sys.colour.content_primary)}
            icon={pd.icon.main.navigation.arrow_forward}
          />
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create(() => ({
  header: {
    paddingBottom: 8,
    paddingHorizontal: 24,
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    minHeight: 44,
  },
  titleContainer: {
    flex: 1,
    marginRight: 12,
    minWidth: 0,
  },
}));
